import { useTranslation } from 'react-i18next';

import type { KeyboardEvent, RefObject } from 'react';

import { Box, styled } from '@mui/material';

import { Spacing } from 'Themes/spacing';

import { Button } from '@/libs/shared/ui';

import { SubmitButton } from './SubmitButton';

const ButtonsContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  gap: ${Spacing.medium};
  justify-content: flex-start;
  align-items: center;
`;

type FormButtonsProps = {
  onSubmit: () => void;
  onCancel?: () => void;
  submitText?: string;
  cancelText?: string;
  isLoading?: boolean;
  loadingText?: string;
  isDisabled?: boolean;
  showCancel?: boolean;
  submitVariant?: 'contained' | 'outlined' | 'text';
  cancelVariant?: 'contained' | 'outlined' | 'text';
  submitButtonRef?: RefObject<HTMLButtonElement | null>;
  onSubmitKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
};

export const FormButtons = ({
  onSubmit,
  onCancel,
  submitText,
  cancelText,
  isLoading = false,
  loadingText,
  isDisabled = false,
  showCancel = true,
  submitVariant = 'contained',
  cancelVariant = 'outlined',
  submitButtonRef,
  onSubmitKeyDown
}: Readonly<FormButtonsProps>) => {
  const { t } = useTranslation();

  const defaultSubmitText = t('Save');
  const defaultCancelText = t('Cancel');

  return (
    <ButtonsContainer>
      <SubmitButton
        variant={submitVariant}
        type="submit"
        onClick={onSubmit}
        onKeyDown={onSubmitKeyDown}
        isLoading={isLoading}
        loadingText={loadingText}
        disabled={isDisabled}
        ref={submitButtonRef}
      >
        {submitText ?? defaultSubmitText}
      </SubmitButton>

      {showCancel && (
        <Button variant={cancelVariant} onClick={onCancel ?? (() => {})} disabled={isLoading}>
          {cancelText ?? defaultCancelText}
        </Button>
      )}
    </ButtonsContainer>
  );
};
